'use client';

import styled from 'styled-components';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import { useHotelMapModal } from '@/app/components/hotel-detail/HotelMapModalProvider';
import { useEffect, useMemo, useRef } from 'react';
import MapContainer from '@/app/components/search/components/SearchPageContainer/MapContainer';
import type { MapRef } from 'react-map-gl/mapbox';
import type { SearchCommonPageData } from '@/app/components/search/SearchMain.tsx';
import { noop } from '@/app/components/search/Utilities.ts';
import { type InitialHighlightHotel } from '@/app/components/search/components/SearchPageContainer/MapContainer/MapComponent';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider.tsx';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { SearchCommonPageContext } from '@/app/components/search/searchCommonPageProvider.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider';
import usePriceDisplay from './hooks/usePriceDisplay.ts';
import { useFetchHotelStatic } from './hooks/useFetchHotelStatic';
import { useFetchSearchProductsForDetailsMap } from './hooks/useFetchSearchProductsForDetailsMap';

export type MapProps = {
  coordinates?: LatLng;
} & React.HTMLAttributes<HTMLElement>;

const MapContainerHotelDetail = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

export default function HotelMap({ coordinates }: MapProps) {
  const { internalHotelId } = useInitialParams();
  const { localization } = usePageWrapperData();
  const { closeMap } = useHotelMapModal();
  const { offersData, isLoadingRooms } = useOffersContext();
  const { data: hotelStaticData } = useFetchHotelStatic();
  const { handleSearchProductsDataChanged } = useCommonClient();
  const { data: searchProducts } = useFetchSearchProductsForDetailsMap();
  const { showTotalPrice } = usePriceDisplay();

  useEffect(() => {
    if (!searchProducts) {
      return;
    }
    handleSearchProductsDataChanged({ ...searchProducts, dots: [] });
  }, [searchProducts]);

  // The surrounding hotels' markers already follow the toggler (getMarkerPrice); this is the highlighted
  // hotel's own marker and popup, which read a single figure.
  const cheapestMealPlan = offersData?.cheapestFilteredMealPlan;
  const cheapestMealPlanFinalPrice =
    (showTotalPrice ? cheapestMealPlan?.finalTotalPrice : cheapestMealPlan?.finalPrice) || 0;

  const handleOtherHotelChosen = () => {
    closeMap();
  };

  const initialHighlightHotel: InitialHighlightHotel = {
    themes: [],
    hotelId: internalHotelId,
    latitude: coordinates?.latitude || 0,
    longitude: coordinates?.longitude || 0,
    cheapestMealPlanFinalPrice,
    currency: localization.currency,
    handleOtherHotelChosen,
    address: '',
    chainName: '',
    comvelId: 0,
    orchestraId: 0,
    facilities: {
      adultsOnly: false,
      children: false,
      disableGuest: false,
      entertainment: false,
      freeWifi: false,
      healthSafety: false,
      kitchen: false,
      parking: false,
      petsAllowed: false,
      pool: false,
      seafront: false,
      spa: false,
    },
    gallery: hotelStaticData?.images || [],
    image: hotelStaticData?.images?.[0] || '',
    isAlternative: false,
    isTopSecret: hotelStaticData?.isTopSecret || false,
    name: hotelStaticData?.name || '',
    providerId: 0,
    rating: hotelStaticData?.hotelOpinion.rating || 0,
    reviewsNum: hotelStaticData?.hotelOpinion.numberOfReviews || 0,
    // Detail map popup uses the detail's own (travel-api) review provider, consistent with the rest
    // of the detail page — so the highlighted hotel's price-marker popup keeps its TY/TA score.
    ratingSource: hotelStaticData?.hotelOpinion.reviewSource,
    stars: hotelStaticData?.rating || 0,
    type: '',
    typeId: 0,
  };

  const searchCommonPageContextValue: SearchCommonPageData = useMemo(
    () => ({
      handleSortChanged: noop,
      handleFiltersChanged: noop,
      handleMapChanged: noop,
    }),
    []
  );

  const mapRef = useRef<MapRef>(null);
  return (
    <MapContainerHotelDetail
      css={`
        .map-container {
          left: 0;
          right: 0;
          bottom: 0;
          top: 56px;
        }
      `}
    >
      <SearchCommonPageContext.Provider value={searchCommonPageContextValue}>
        <MapContainer
          isWaiting={isLoadingRooms}
          isLoading={false}
          mapRef={mapRef}
          showFilters={false}
          cardOverHotelId={undefined}
          dynamicContentResponse={undefined}
          forceFullscreenMapDesktop={true}
          showCloseResize={false}
          initialHighlightHotel={initialHighlightHotel}
          shouldLaunchSearchChanged={false}
        />
      </SearchCommonPageContext.Provider>
    </MapContainerHotelDetail>
  );
}
